import {
  SubscriptionFlowDefinition,
  type FlowAnswers,
  type RecapPolicy,
} from './SubscriptionFlowDefinition'
import type { AvailableBundlesParams } from '@/services/subscription'

/**
 * Solidarité Exam journey (`/solidarite-exam`, subcategory SOLIDARITE_EXAM).
 *
 * Mirrors the health-expenses gating questions (for whom / student / age) but the
 * backend decision tree (DecisionTree::SolidariteExam) ignores every input and
 * always returns the single Solidarité exam bundle, so `available_bundles` only
 * needs the subcategory. There is no cross-sell and no top-up.
 *
 * An annual cover paid upfront or monthly that does NOT renew tacitly (contractual
 * renewal), with the STUD'LIFE club reassurance block on the recap. Neither SSN
 * nor banking details are collected. The period is the standard start date over
 * `duration` months (fixed to 12) and the price is the standard monthly/annual
 * rate, so neither `period()` nor `displayPrice()` is overridden.
 */
export class SolidariteExamFlow extends SubscriptionFlowDefinition {
  readonly product = 'solidariteExam' as const
  readonly subcategory = 'SOLIDARITE_EXAM' as const

  /** Source of truth: DecisionTree::SolidariteExam on the backend (single bundle). */
  readonly planReference: Record<string, number> = {
    exam: 34,
  }

  /** Annual cover paid upfront or monthly, contractual (non-tacit) renewal, with
   *  the club block shown. */
  readonly recap: RecapPolicy = {
    showPaymentSelector: true,
    showClub: true,
    tacitRow: 'no',
    priceMode: 'duration',
  }

  /** The cover renews contractually, not tacitly. */
  readonly tacitRenewal = false

  bundlesParams(a: FlowAnswers): AvailableBundlesParams | null {
    // The tree needs no answer, but we wait until the questionnaire reaches the
    // results step (age answered) before querying, mirroring the other flows.
    if (!a.age) return null
    return { subcategory: 'SOLIDARITE_EXAM' }
  }
}
